import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  Divider,
  MenuItem,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  History as HistoryIcon,
  TrendingUp as TrendingIcon,
  Label as LabelIcon,
  FilterList as FilterIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useIsMobile } from '../../hooks/useResponsive';
import LabelStorage from '../../utils/labelStorage';

/**
 * Enhanced Search Bar with autocomplete, search history, and smart suggestions
 *
 * Features:
 * - Search history with autocomplete
 * - Label-based suggestions
 * - Search operators (type:, label:, devices:)
 * - Fuzzy matching
 * - Keyboard navigation
 */
const EnhancedSearchBar = ({
  onSearch,
  onFilterChange,
  placeholder = 'Search locations...',
  autoFocus = false,
  isSearching = false,
  initialValue = '',
}) => {
  const isMobile = useIsMobile();
  // Ensure initialValue is always a string to prevent .trim() errors
  const safeInitialValue = typeof initialValue === 'string' ? initialValue : '';
  const [inputValue, setInputValue] = useState(safeInitialValue);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const searchTimeout = useRef(null);

  // Update input value when initialValue changes (for filter persistence)
  useEffect(() => {
    setInputValue(typeof initialValue === 'string' ? initialValue : '');
  }, [initialValue]);

  // Debounced search (300ms delay)
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      if (onSearch) {
        onSearch(inputValue);
      }

      // Save to history if not empty
      const safeValue = typeof inputValue === 'string' ? inputValue : '';
      if (safeValue && safeValue.trim().length > 0) {
        LabelStorage.addToSearchHistory(safeValue);
      }
    }, 300); // Wait 300ms after user stops typing

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [inputValue, onSearch]);

  // Get search history and label suggestions
  const searchHistory = useMemo(() => LabelStorage.getRecentSearches(5), [inputValue]);
  const allLabels = useMemo(() => LabelStorage.getAllUniqueLabelNames(), [inputValue]);

  // Generate suggestions based on input
  const suggestions = useMemo(() => {
    // Ensure inputValue is a string before calling string methods
    const safeInputValue = typeof inputValue === 'string' ? inputValue : '';
    if (!safeInputValue || safeInputValue.trim().length === 0) {
      // Show recent searches when empty
      return searchHistory.map(term => ({
        type: 'history',
        text: term,
        icon: <HistoryIcon fontSize="small" />,
        label: 'Recent search',
      }));
    }

    const input = safeInputValue.toLowerCase().trim();
    const suggestions = [];

    // Check for search operators
    if (input.includes(':')) {
      const [operator, value] = input.split(':').map(s => s.trim());

      if (operator === 'label' && value) {
        // Label search suggestions
        const matchingLabels = allLabels.filter(label =>
          label.name.toLowerCase().includes(value)
        );

        matchingLabels.forEach(label => {
          suggestions.push({
            type: 'label-search',
            text: `label:${label.name}`,
            icon: <LabelIcon fontSize="small" sx={{ color: label.color }} />,
            label: `${label.count} location${label.count > 1 ? 's' : ''}`,
            color: label.color,
          });
        });
      } else if (operator === 'type') {
        // Location type suggestions
        const types = ['site', 'building', 'floor', 'unit', 'room', 'hallway', 'restroom', 'stock'];
        const matchingTypes = value
          ? types.filter(t => t.includes(value))
          : types;

        matchingTypes.forEach(type => {
          suggestions.push({
            type: 'type-search',
            text: `type:${type}`,
            icon: <FilterIcon fontSize="small" />,
            label: 'Location type',
          });
        });
      } else if (operator === 'devices') {
        // Device count filter suggestions
        const operators = ['>', '<', '>=', '<=', '='];
        operators.forEach(op => {
          suggestions.push({
            type: 'device-search',
            text: `devices:${op}${value || '0'}`,
            icon: <FilterIcon fontSize="small" />,
            label: 'Device count filter',
          });
        });
      }
    } else {
      // Regular search - show matching history and label suggestions
      const matchingHistory = searchHistory.filter(term =>
        term.toLowerCase().includes(input)
      );

      matchingHistory.forEach(term => {
        suggestions.push({
          type: 'history',
          text: term,
          icon: <HistoryIcon fontSize="small" />,
          label: 'Recent search',
        });
      });

      // Add label suggestions
      const matchingLabels = allLabels.filter(label =>
        label.name.toLowerCase().includes(input)
      );

      if (matchingLabels.length > 0) {
        matchingLabels.slice(0, 3).forEach(label => {
          suggestions.push({
            type: 'label-suggestion',
            text: label.name,
            icon: <LabelIcon fontSize="small" sx={{ color: label.color }} />,
            label: `Label (${label.count} location${label.count > 1 ? 's' : ''})`,
            color: label.color,
          });
        });
      }

      // Add operator hints if input looks like it could be a command
      if (input.length > 0 && !input.includes(' ')) {
        const operators = ['label:', 'type:', 'devices:'];
        const matchingOps = operators.filter(op => op.startsWith(input));

        matchingOps.forEach(op => {
          suggestions.push({
            type: 'operator-hint',
            text: op,
            icon: <FilterIcon fontSize="small" color="primary" />,
            label: 'Search operator',
          });
        });
      }
    }

    return suggestions.slice(0, 8); // Limit to 8 suggestions
  }, [inputValue, searchHistory, allLabels]);

  // Handle input change
  const handleInputChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    setShowSuggestions(true);
    setHighlightedIndex(0);
    // Search is now triggered by useEffect with debounce
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    setInputValue(suggestion.text);
    setShowSuggestions(false);

    // Save to history
    LabelStorage.addToSearchHistory(suggestion.text);

    // Trigger search
    if (onSearch) {
      onSearch(suggestion.text);
    }

    // Focus back on input
    inputRef.current?.focus();
  };

  // Handle clear
  const handleClear = () => {
    setInputValue('');
    setShowSuggestions(false);
    setHighlightedIndex(0);

    if (onSearch) {
      onSearch('');
    }

    inputRef.current?.focus();
  };

  // Keyboard navigation
  const handleKeyDown = (event) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (event.key === 'Enter' && inputValue) {
        // Save search and close suggestions
        LabelStorage.addToSearchHistory(inputValue);
        setShowSuggestions(false);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;

      case 'Enter':
        event.preventDefault();
        if (suggestions[highlightedIndex]) {
          handleSelectSuggestion(suggestions[highlightedIndex]);
        }
        break;

      case 'Escape':
        setShowSuggestions(false);
        break;

      default:
        break;
    }
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        !inputRef.current?.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Box sx={{ position: 'relative', flex: 1, maxWidth: { xs: '100%', sm: 600 }, width: '100%' }}>
      <TextField
        ref={inputRef}
        fullWidth
        size={isMobile ? "medium" : "small"}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowSuggestions(true)}
        autoFocus={!isMobile && autoFocus}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {isSearching ? (
                <CircularProgress size={isMobile ? 24 : 20} />
              ) : (
                <SearchIcon fontSize={isMobile ? "medium" : "small"} />
              )}
            </InputAdornment>
          ),
          endAdornment: inputValue && (
            <InputAdornment position="end">
              <IconButton
                size={isMobile ? "medium" : "small"}
                onClick={handleClear}
                sx={{
                  minWidth: { xs: 44, sm: 'auto' },
                  minHeight: { xs: 44, sm: 'auto' },
                }}
              >
                <ClearIcon fontSize={isMobile ? "medium" : "small"} />
              </IconButton>
            </InputAdornment>
          ),
          sx: {
            backgroundColor: theme => theme.palette.background.default,
            minHeight: { xs: 48, sm: 40 },
          },
        }}
      />

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <Paper
          ref={suggestionsRef}
          elevation={8}
          sx={{
            position: isMobile ? 'fixed' : 'absolute',
            top: isMobile ? 'auto' : '100%',
            bottom: isMobile ? 0 : 'auto',
            left: 0,
            right: 0,
            mt: isMobile ? 0 : 0.5,
            zIndex: 1300,
            maxHeight: isMobile ? '60vh' : 400,
            overflow: 'auto',
            ...(isMobile && {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }),
          }}
        >
          <List dense={!isMobile}>
            {suggestions.map((suggestion, index) => (
              <ListItem
                key={`${suggestion.type}-${index}`}
                button
                selected={index === highlightedIndex}
                onClick={() => handleSelectSuggestion(suggestion)}
                onMouseEnter={() => setHighlightedIndex(index)}
                sx={{
                  minHeight: { xs: 56, sm: 48 },
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: { xs: 48, sm: 36 } }}>
                  {suggestion.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant={isMobile ? "body1" : "body2"}>
                        {suggestion.text}
                      </Typography>
                      {suggestion.color && (
                        <Box
                          sx={{
                            width: { xs: 16, sm: 12 },
                            height: { xs: 16, sm: 12 },
                            borderRadius: '50%',
                            backgroundColor: suggestion.color,
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant={isMobile ? "body2" : "caption"} color="text.secondary">
                      {suggestion.label}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>

          {/* Search tips - hide on mobile to save space */}
          {!isMobile && (
            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                💡 <strong>Tip:</strong> Try <code>label:maintenance</code>, <code>type:room</code>, or <code>devices:&gt;0</code>
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default EnhancedSearchBar;
